import { SeoController } from '@application/components';
import { CACHE_KEY_QUIZ_ANSWERS } from '@application/constants';
import { useRouter } from '@application/utils';
import type { SelectOption } from '@presentation/components';
import type { QuizPageStep } from '@presentation/pages';
import { QuizPage } from '@presentation/pages';
import { omit } from 'lodash/fp';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';

export const QuizPageController: FC = () => {
  const { translate, infoFromCurrentPath, navigateToRoute } = useRouter();
  const [answers, setAnswers] = useState(JSON.parse(localStorage.getItem('quizzAnswers') ?? '{}'));

  useEffect(() => {
    localStorage.setItem('quizzAnswers', JSON.stringify(answers));
  }, [answers]);

  const allSteps = useMemo((): QuizPageStep[] => {
    const translations = translate('quiz.steps', { returnObjects: true });
    const keys = Object.keys(translations);
    const steps: QuizPageStep[] = [];
    for (let i = 0; i < keys.length; i += 1) {
      const value = translations[keys[i] as any] as any;
      const { question, result: answerType, tip, skippable: rawSkippable } = value;

      const propositions = Object.keys(value.propositions).map(
        (pKey) =>
          ({
            value: pKey,
            label: value.propositions[pKey],
          } as SelectOption<string>),
      );

      const skippable = rawSkippable === 'YES';

      steps.push({ question, propositions, answerType, tip, skippable } as QuizPageStep);
    }
    return steps;
  }, [translate]);

  const { pathname } = useLocation();

  const maxStep = useMemo(() => {
    const maxStepFromState = Object.keys(answers)
      .filter((a) => !Number.isNaN(Number(a)))
      .map((a) => Number(a) + 1)
      .reduce((max, value) => (value > max ? value : max), 0);
    return Math.min(maxStepFromState, allSteps.length - 1);
  }, [allSteps.length, answers]);

  const setCurrentStep = useCallback(
    (step: number) => {
      navigateToRoute('quiz', { params: [(step + 1).toString()] });
    },
    [navigateToRoute],
  );

  const currentStep = useMemo(() => {
    const param = infoFromCurrentPath.params[0];
    const stepFromParam = Number.isNaN(Number(param)) ? 0 : Number(param) - 1;
    if (stepFromParam > maxStep) {
      setCurrentStep(maxStep);
    }
    return Math.min(stepFromParam, maxStep);
  }, [infoFromCurrentPath.params, maxStep, setCurrentStep]);

  const answerSelectHandler = useCallback(
    (answer: string | null) => {
      if (answers[currentStep] !== answer) {
        setAnswers({ ...answers, [currentStep]: answer });
      } else {
        setAnswers(omit(currentStep, answers));
      }
    },
    [answers, currentStep, setAnswers],
  );

  const submit = useCallback(() => {
    localStorage.setItem(CACHE_KEY_QUIZ_ANSWERS, JSON.stringify(answers));
    navigateToRoute('quizResults');
  }, [answers, navigateToRoute]);

  const nextClickHandler = useCallback(() => {
    if (currentStep < maxStep) {
      setCurrentStep(currentStep + 1);
    } else {
      submit();
    }
  }, [currentStep, maxStep, setCurrentStep, submit]);

  const previousClickHandler = useCallback(() => {
    if (currentStep === 0) return;
    const newAnswers = { ...answers };
    delete newAnswers[currentStep];
    if (newAnswers[currentStep - 1] === null) {
      delete newAnswers[currentStep - 1];
    }
    setAnswers(newAnswers);
    setCurrentStep(currentStep - 1);
  }, [answers, currentStep, setAnswers, setCurrentStep]);

  useEffect(() => {
    if (currentStep === 0) return;
    setTimeout(() => {
      if (typeof window === 'undefined') return;
      const element = document.getElementById('quiz-question-title');
      if (!element) return;
      const yOffset = -100;
      const scrollY = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: scrollY, behavior: 'smooth' });
    }, 100);
  }, [currentStep, pathname]);

  return (
    <>
      <SeoController
        title={translate('quiz.seo.title')}
        description={translate('quiz.seo.description')}
      />
      <QuizPage
        currentStep={currentStep}
        steps={allSteps}
        answers={answers}
        onAnswerSelect={answerSelectHandler}
        onNextClick={nextClickHandler}
        onPreviousClick={previousClickHandler}
      />
    </>
  );
};
